import { useState, useRef, useEffect } from "react"
import { cn } from "@/lib/utils"
import { HighlightText } from "@/components/highlight-text"
import { slideAlternatingBlocks, slideInFrom, sectionHeaderClasses } from "@/lib/section-slide"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

const stats = [
  { label: "Tape-Outs", count: { type: "plus" as const, to: 20 } },
  { label: "Nm Range", count: { type: "range" as const, from: 180, to: 5 }, animate: false },
  { label: "Team Size", count: { type: "plus" as const, to: 30 } },
  { label: "Yrs Avg. Exp.", count: { type: "range" as const, from: 15, to: 20 } },
]

const expertiseAreas = [
  {
    title: "Foundries & Nodes",
    summary: "Production silicon across leading foundries from 180nm to 5nm.",
    tags: [
      "TSMC · GF · Samsung · Intel",
      "5 · 6 · 12 · 16 · 22 · 22FDX · 28nm",
      "40 · 55 · 65 · 90 · 180nm",
    ],
  },
  {
    title: "Application Domains",
    summary: "Deep delivery experience across compute, sensing and high-reliability silicon.",
    tags: ["CPU · GPU · AI accelerators", "ADAS · Mixed-signal", "Surveillance · Space"],
  },
  {
    title: "Full-Chip Expertise",
    summary: "End-to-end physical design ownership from floorplan through signoff.",
    tags: ["STA & timing closure", "IR/EM · DRC/LVS", "PPA · Hierarchical PD"],
  },
  {
    title: "Tools & Methodology",
    summary: "Mature CAD ownership across industry-standard EDA toolchains and flows.",
    tags: ["Synopsys · Cadence", "Ansys · Siemens", "Flow automation"],
  },
]

export function CredibilitySection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const headlineRef = useRef<HTMLHeadingElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)
  const expertiseRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLAnchorElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const [isHoveringExpertise, setIsHoveringExpertise] = useState(false)

  useEffect(() => {
    if (!expertiseRef.current || !cursorRef.current) return

    const zone = expertiseRef.current
    const cursor = cursorRef.current

    const handleMouseMove = (e: MouseEvent) => {
      const rect = zone.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      gsap.to(cursor, {
        x,
        y,
        duration: 0.5,
        ease: "power3.out",
      })
    }

    const handleMouseEnter = () => setIsHoveringExpertise(true)
    const handleMouseLeave = () => setIsHoveringExpertise(false)

    zone.addEventListener("mousemove", handleMouseMove)
    zone.addEventListener("mouseenter", handleMouseEnter)
    zone.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      zone.removeEventListener("mousemove", handleMouseMove)
      zone.removeEventListener("mouseenter", handleMouseEnter)
      zone.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [])

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !statsRef.current || !expertiseRef.current) return

    const ctx = gsap.context(() => {
      slideInFrom(headerRef.current, "left")

      const headlineLines = headlineRef.current?.querySelectorAll("[data-headline-line]")
      if (headlineLines?.length) {
        gsap.fromTo(
          headlineLines,
          { y: 48, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.9,
            stagger: 0.18,
            delay: 0.2,
            ease: "power3.out",
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }

      slideInFrom(statsRef.current, "left", { delay: 0.1 })
      slideInFrom(footerRef.current, "left", { delay: 0.2 })

      const statCells = statsRef.current?.querySelectorAll("[data-stat-cell]")
      if (statCells?.length) {
        const runCountUp = () => {
          statCells.forEach((cell, index) => {
            const valueEl = cell.querySelector<HTMLSpanElement>("[data-stat-value]")
            if (!valueEl) return
            if (valueEl.dataset.statAnimate === "false") return

            const type = valueEl.dataset.statType
            if (type === "plus") {
              const to = Number(valueEl.dataset.statTo ?? 0)
              const counter = { n: 0 }
              gsap.to(counter, {
                n: to,
                duration: 2.2,
                delay: index * 0.15,
                ease: "power2.out",
                snap: { n: 1 },
                onUpdate: () => {
                  valueEl.textContent = `${Math.round(counter.n)}+`
                },
              })
            } else if (type === "range") {
              const from = Number(valueEl.dataset.statFrom ?? 0)
              const to = Number(valueEl.dataset.statTo ?? 0)
              const counter = { a: 0, b: 0 }
              gsap.to(counter, {
                a: from,
                b: to,
                duration: 2.2,
                delay: index * 0.15,
                ease: "power2.out",
                snap: { a: 1, b: 1 },
                onUpdate: () => {
                  valueEl.textContent = `${Math.round(counter.a)}-${Math.round(counter.b)}`
                },
              })
            }
          })
        }

        const countTrigger = ScrollTrigger.create({
          trigger: statsRef.current,
          start: "top 85%",
          once: true,
          onEnter: runCountUp,
        })

        if (countTrigger.isActive) runCountUp()
      }

      slideAlternatingBlocks(expertiseRef.current, "article", "left")
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  useEffect(() => {
    ScrollTrigger.refresh()
  }, [])

  return (
    <section ref={sectionRef} id="credibility" className="relative border-t border-border/30 py-32 pl-6 md:pl-28 pr-6 md:pr-12">
      <div ref={headerRef} className={cn("mb-16 max-w-3xl", sectionHeaderClasses("left"))}>
        <span ref={labelRef} className="mb-4 block font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          The Team
        </span>
        <h2
          ref={headlineRef}
          className="mt-4 font-[var(--font-bebas)] text-5xl leading-[1.05] tracking-tight uppercase md:text-7xl"
        >
          <span data-headline-line className="block">
            <HighlightText>An Expert Team.</HighlightText>
          </span>
        </h2>
        <p className="mt-6 font-mono text-xs leading-relaxed text-muted-foreground md:text-sm">
          Diverse full-chip expertise across foundries, nodes, applications and EDA flows — built for complex tape-outs,
          not temporary staffing.
        </p>
      </div>

      <div ref={statsRef} className="grid grid-cols-2 border border-border/30 md:grid-cols-4">
        {stats.map((stat) => (
          <StatCell key={stat.label} stat={stat} />
        ))}
      </div>

      <div ref={expertiseRef} className="relative mt-px border border-t-0 border-border/30">
        <div
          ref={cursorRef}
          className={cn(
            "pointer-events-none absolute top-0 left-0 z-50 -translate-x-1/2 -translate-y-1/2",
            "h-12 w-12 rounded-full border-2 border-accent bg-accent",
            "transition-opacity duration-300",
            isHoveringExpertise ? "opacity-100" : "opacity-0",
          )}
        />

        <div className="grid grid-cols-1 gap-px bg-border/30 sm:grid-cols-2 md:grid-cols-4">
          {expertiseAreas.map((area) => (
            <ExpertiseCard key={area.title} area={area} />
          ))}
        </div>
      </div>

      <a
        ref={footerRef}
        href="#close"
        className="mt-10 inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-accent transition-colors duration-300 hover:text-foreground"
      >
        Request Detailed Team Background →
      </a>
    </section>
  )
}

type StatCount =
  | { type: "plus"; to: number }
  | { type: "range"; from: number; to: number }

function StatCell({ stat }: { stat: { label: string; count: StatCount; animate?: boolean } }) {
  const initialDisplay =
    stat.animate === false && stat.count.type === "range"
      ? `${stat.count.from}-${stat.count.to}`
      : stat.count.type === "plus"
        ? "0+"
        : "0-0"

  return (
    <div data-stat-cell className="flex flex-col justify-center border-b border-r border-border/30 p-6 md:p-8">
      <span
        data-stat-value
        data-stat-type={stat.count.type}
        data-stat-animate={stat.animate !== false}
        data-stat-to={stat.count.type === "plus" ? stat.count.to : stat.count.to}
        data-stat-from={stat.count.type === "range" ? stat.count.from : undefined}
        className="font-[var(--font-bebas)] text-4xl tracking-tight text-accent tabular-nums md:text-5xl"
      >
        {initialDisplay}
      </span>
      <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{stat.label}</span>
    </div>
  )
}

function ExpertiseCard({
  area,
}: {
  area: { title: string; summary: string; tags: string[] }
}) {
  return (
    <article
      className={cn(
        "group relative flex-shrink-0",
        "transition-transform duration-500 ease-out",
        "hover:-translate-y-2",
      )}
    >
      <div className="relative flex h-full flex-col bg-card/90 p-6 md:p-8">
        <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />

        <div className="mb-6 flex items-center justify-between">
          <div className="h-8 w-8 rounded-full border border-border/60 bg-secondary/30 transition-colors duration-300 group-hover:border-accent/60 group-hover:bg-accent/10" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60">Expertise</span>
        </div>

        <h3 className="font-[var(--font-bebas)] text-xl tracking-tight uppercase transition-colors duration-300 group-hover:text-accent md:text-2xl">
          {area.title}
        </h3>

        <p className="mt-3 font-mono text-[10px] leading-relaxed text-muted-foreground md:text-xs">{area.summary}</p>

        <div className="my-4 h-px w-12 bg-accent/60 transition-all duration-500 group-hover:w-full" />

        <div className="mt-auto flex flex-wrap gap-2">
          {area.tags.map((tag) => (
            <span
              key={tag}
              className="border border-border/40 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 group-hover:border-accent/30 group-hover:text-foreground/80"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="absolute bottom-0 right-0 h-6 w-6 overflow-hidden">
          <div className="absolute bottom-0 right-0 h-8 w-8 translate-x-4 translate-y-4 rotate-45 border-t border-l border-border/30 bg-background" />
        </div>
      </div>

      <div className="absolute inset-0 -z-10 translate-x-1 translate-y-1 bg-accent/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </article>
  )
}
